import moment from 'moment';
import { CARD_TYPE_MAP, PAYMENT_OPTIONS } from './paymentConstant';

export function getPreferredEmail(subscribeData) {
  // Extract primary email contacts
  const primaryEmails =
    subscribeData?.subscriberContacts
      ?.filter(
        (contact) =>
          (contact.contactType === 'EMAIL' || contact.contactMethod === 'E') &&
          contact.primaryContact === 'Y'
      )
      .map((contact) => contact.contactInfo) || [];

  // If no primary email exists, use the first available email as fallback
  if (primaryEmails.length === 0) {
    const fallbackEmail = subscribeData?.subscriberContacts?.find(
      (contact) =>
        contact.contactType === 'EMAIL' || contact.contactMethod === 'E'
    )?.contactInfo;
    return fallbackEmail || null; // Return fallback email or null if no email exists
  }

  // Return the first primary email
  return primaryEmails[0];
}

// Generating the token string from the JSON-like object
export const generatePaymentusIframeSSOToken = (data) => {
  const tokenParts = [];

  Object.entries(data).forEach(([key, value]) => {
    if (typeof value === 'object' && !Array.isArray(value)) {
      // Nested object handling (e.g., customer or header)
      Object.entries(value).forEach(([subKey, subValue]) => {
        if (typeof subValue === 'object') {
          // Further nested object (e.g., address inside customer)
          Object.entries(subValue).forEach(([innerKey, innerValue]) => {
            tokenParts.push(`${key}.${subKey}.${innerKey}=${innerValue}`);
          });
        } else {
          tokenParts.push(`${key}.${subKey}=${subValue}`);
        }
      });
    } else {
      tokenParts.push(`${key}=${value}`);
    }
  });

  return tokenParts.join('; ');
};

export const generateDateTime = (dateString) => {
  const format = 'MMDDYYYYHHmmss';
  const momentObj = moment(dateString, format);

  if (!momentObj.isValid()) {
    return 'Invalid date';
  }

  return momentObj.format('YYYY-MM-DD-HH.mm.ss');
};

export const generateDateTime_MDY = (dateString) => {
  const format = 'MMDDYYYYHHmmss';
  const momentObj = moment(dateString, format);

  if (!momentObj.isValid()) {
    return 'Invalid date';
  }

  return momentObj.format('MM/DD/YYYY HH:mm:ss A');
};

export const format_unix = (timestamp) => {
  const momentObj = moment.unix(timestamp);

  if (!momentObj.isValid()) {
    return 'Invalid date';
  }

  return momentObj.format('MM/DD/YYYY hh:mm:ss A'); // Formatted output
};

export const format_unix_dMY = (timestamp) => {
  const momentObj = moment.unix(timestamp);

  if (!momentObj.isValid()) {
    return 'Invalid date';
  }

  return momentObj.format('DD MMMM YYYY'); // Full month name
};

export const formatDateForDB = (date) => {
  const pad = (num) => String(num).padStart(2, '0');

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1); // Months are zero-indexed
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const getCardBrand = (cardType) => {
  switch (cardType) {
    case 'VISA':
    case 'VISA_DEBIT':
      return 'VI';
    case 'MASTERCARD':
    case 'MC_DEBIT':
      return 'MC';
    case 'DISCOVER':
    case 'DISC_DEBIT':
      return 'DI';
    case 'AMEX':
      return 'AX';
    default:
      return 'Unknown'; // Default case for unhandled card types
  }
};

export const getPaymentTypeCode = (cardType) => {
  switch (cardType) {
    case 'VI':
    case 'VISA':
      return 'PHONEVC';
    case 'MC':
      return 'PHONEMC';
    case 'DISCOVER':
    case 'DISC':
      return 'PHONEDC';
    case 'AMEX':
    case 'AX':
      return 'PHONEAC';
    case 'SAV':
    case 'CHQ':
      return 'PHONEACH';
    default:
      return 'PHONE'; // Default case for unhandled card types
  }
};

export const generateCardExpiryDateTime = (expiryDate) => {
  const inputFormat = 'MM/YYYY'; // Input format for expiry date
  const momentObj = moment(expiryDate, inputFormat);

  if (!momentObj.isValid()) {
    return 'Invalid date'; // Return this if the date string doesn't match the format or is invalid
  }

  const lastDayOfMonth = momentObj.endOf('month').format('YYYY-MM-DD-HH.mm.ss');

  return lastDayOfMonth;
};

export const generateCardExpiryDateTimeMY = (expiryDate) => {
  const inputFormat = 'MM/YYYY'; // Input format for expiry date
  const momentObj = moment(expiryDate, inputFormat);

  if (!momentObj.isValid()) {
    return 'Invalid date'; // Return this if the date string doesn't match the format or is invalid
  }

  return momentObj.format('MMMM YYYY'); // Full month name
};

// Function to get the image based on cardType
export const getCardImage = (cardType) => {
  // Map the cardType to standardized value
  if (cardType == 'MC') cardType = 'MASTERCARD';
  const mappedType = CARD_TYPE_MAP[cardType.toUpperCase()] || 'default-card';

  // Find the matching payment option based on value
  const selectedCard = PAYMENT_OPTIONS.find(
    (option) => option.value === mappedType
  );
  // Return the icon or a default placeholder
  return selectedCard ? selectedCard.icon : '';
};

